import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const UploadDocuments = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, staggerChildren: 0.08 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    if (!isUploading) {
      setFile(null);
      setIsOpen(false);
    }
  };

  const handleFiles = files => {
    if (!files || !files.length) return;
    setFile(files[0]);
    setIsUploading(true);
    setTimeout(() => {
      setIsUploading(false);
      setIsOpen(false);
      setFile(null);
    }, 2500);
  };

  const onDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setDragActive(true);
  };

  const onDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onFileInputChange = e => handleFiles(e.target.files);

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="mx-auto w-[92%] max-w-2xl rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:w-full md:p-5"
      >
        <motion.div className="mb-3 flex items-center" variants={itemVariants}>
          <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
            <UploadIcon className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-black">Upload Documents</h2>
        </motion.div>

        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ type: 'spring', stiffness: 400 }}
          className="mb-3 flex flex-col items-start rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <div className="mb-2 flex items-center">
            <UploadIcon className="mr-2 h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-black">No Data Available</span>
          </div>
          <p className="mb-3 text-xs text-black">
            To get started with People Pulse Analytix, please upload your employee data file. For
            Excel files (.xlsx), both company and employee data will be uploaded from their
            respective sheets. For CSV files, only company data will be uploaded.
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openModal}
            className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <UploadIcon className="mr-2 h-4 w-4" /> Upload Data File
          </motion.button>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3 }}
              className="w-[90%] max-w-lg rounded-xl bg-white p-6 shadow-xl"
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragLeave={onDragLeave}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black">Upload Data File</h3>
                <button
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={isUploading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <label
                htmlFor="fileInput"
                className={`flex h-56 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-100'
                } text-center transition-colors`}
              >
                {file ? (
                  <>
                    <UploadIcon className="mb-3 h-8 w-8 text-blue-600" />
                    <span className="text-sm font-medium text-black">{file.name}</span>
                  </>
                ) : (
                  <>
                    <UploadIcon className="mb-3 h-8 w-8 text-gray-500" />
                    <p className="mb-1 text-sm font-medium text-black">
                      Drag &amp; drop files or{' '}
                      <span className="text-blue-600 underline">Choose file</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Supported: XLS, XLSX, CSV, JSON, PDF, DOC, DOCX &nbsp;|&nbsp; Max 10&nbsp;MB
                    </p>
                  </>
                )}
              </label>
              <input
                id="fileInput"
                type="file"
                accept=".xls,.xlsx,.csv,.json,.pdf,.doc,.docx"
                className="hidden"
                onChange={onFileInputChange}
              />

              <AnimatePresence>
                {isUploading && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 8 }}
                    exit={{ height: 0 }}
                    className="mt-6 overflow-hidden rounded bg-gray-200"
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 2.5, ease: 'easeInOut' }}
                      className="h-full bg-blue-600"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const UploadIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V8m0 0l-4 4m4-4l4 4" />
    <rect x="4" y="4" width="16" height="16" rx="4" />
  </svg>
);

export default UploadDocuments;
